import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import API from "../../../../api/Axios";
import DataLoader from "../../../../shared/DataLoader/DataLoader";

// Helper function to render text with clickable links
const renderWithLinks = (text: string) => {
  const urlRegex =
    /(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)|(www\.[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, idx) => {
    if (!part) return null;
    if (part.match(urlRegex)) {
      const href = part.startsWith("http") ? part : `https://${part}`;
      return (
        <a
          key={idx}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1976d2", wordBreak: "break-all" }}
        >
          {part}
        </a>
      );
    }
    return <span key={idx}>{part}</span>;
  });
};

const FAQPage = () => {
  const [expanded, setExpanded] = useState<number | false>(0);
  const [faqData, setFAqData] = useState([]);
  const [faqDataLoading, setFaqDataLoading] = useState(true);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { t } = useTranslation("home");
  const handleChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };
  const getFAQData = async () => {
    setFaqDataLoading(true);
    try {
      const response = await API.get("/web/get-faq");
      setFAqData(response?.data?.data);
      console.log("faQ response", response);
    } catch (error) {
      console.log("error fetching faqData", faqData);
    } finally {
      setFaqDataLoading(false);
    }
  };

  useEffect(() => {
    getFAQData();
  }, []);

  return (
    <>
      {faqDataLoading ? (
        <Box
          sx={{
            minHeight: "80vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
          }}
        >
          <DataLoader />
        </Box>
      ) : (
        <Box
          sx={{
            background: "transparent",
            pt: { xs: 3, md: "45px" },
            pb: { xs: 7, md: 10 },
            transition: "background 0.3s",
          }}
        >
          <Container
            maxWidth={false}
            sx={{ width: { xs: "100%", sm: "94%", md: "88%" } }}
          >
            <Typography
              fontWeight={700}
              sx={{
                mb: { xs: 3, md: 4 },
                color: theme.palette.text.primary,
                textAlign: "left",
                fontSize: {
                  xs: `calc(34px * var(--dynamic-font-size, 1))`,
                  md: `calc(48px * var(--dynamic-font-size, 1))`,
                },
                lineHeight: 1.15,
                transition: "color 0.3s",
              }}
            >
              {t("faq.title")}
            </Typography>
            <Box
              sx={{
                p: { xs: 2, md: 3 },
                border: `1px solid ${
                  isDarkMode ? "rgba(184,184,184,.22)" : "#cfe9d3"
                }`,
                borderRadius: { xs: "22px", md: "32px" },
                backgroundColor: isDarkMode ? "#202624" : "#f5fff4",
              }}
            >
              {faqData.map((faq: any, idx: number) => (
                <Accordion
                  key={idx}
                  expanded={expanded === idx}
                  onChange={handleChange(idx)}
                  sx={{
                    mb: { xs: 1.5, md: 2 },
                    borderRadius: "18px !important",
                    boxShadow: "none",
                    background: isDarkMode ? "#29302e" : "#ffffff",
                    border: `1px solid ${
                      isDarkMode ? "rgba(184,184,184,.16)" : "#cfe9d3"
                    }`,
                    transition: "border 0.2s, background 0.2s",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          display: "grid",
                          placeItems: "center",
                          bgcolor: expanded === idx
                            ? isDarkMode
                              ? "#777"
                              : "#1A5F20"
                            : "transparent",
                          color:
                            expanded === idx
                              ? "#fff"
                              : isDarkMode
                              ? "#b8b8b8"
                              : "#1A5F20",
                          border: `1px solid ${
                            isDarkMode ? "#a8a8a8" : "#1A5F20"
                          }`,
                          borderRadius: "50%",
                          width: { xs: 28, md: 32 },
                          height: { xs: 28, md: 32 },
                          "& svg": { fontSize: { xs: 18, md: 21 } },
                        }}
                      >
                        {expanded === idx ? <RemoveIcon /> : <AddIcon />}
                      </Box>
                    }
                    aria-controls={`panel${idx}-content`}
                    id={`panel${idx}-header`}
                    sx={{
                      background: isDarkMode ? "#27332d" : "#e4f8e8",
                      minHeight: { xs: 54, md: 62 },
                      px: { xs: 2, md: 3 },
                      py: 1,
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        my: 1.25,
                      },
                    }}
                  >
                    <Typography
                      component="strong"
                      sx={{
                        minWidth: { xs: 28, md: 28 },
                        color: isDarkMode ? "#e9eeeb" : "#183122",
                        fontSize: {
                          xs: `calc(13px * var(--dynamic-font-size,1))`,
                          md: `calc(15px * var(--dynamic-font-size,1))`,
                        },
                        fontWeight:'600'
                      }}
                    >
                      {String(idx + 1).padStart(2, "0")}.
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        fontSize: {
                          xs: `calc(0.875rem *var(--dynamic-font-size,1))`,
                          md: `calc(15px * var(--dynamic-font-size,1))`,
                        },
                        transition: "color 0.3s",
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  {faq.answer && (
                    <AccordionDetails
                      sx={{
                        background: isDarkMode ? "#252b29" : "#ffffff",
                        color: theme.palette.text.secondary,
                        fontSize: {
                          xs: `calc(0.75rem *var(--dynamic-font-size,1))`,
                          md: `calc(1rem *var(--dynamic-font-size,1))`,
                        },
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: { xs: 2.5, md: 3.5 },
                        py: { xs: 2, md: 2.5 },
                        transition: "background 0.3s, color 0.3s",
                      }}
                    >
                      {(() => {
                        return faq.answer.split("\n").map((line, idx) => {
                          // Check if the line starts with a number followed by a dot
                          const numberMatch = line.trim().match(/^(\d+\.)\s*/);
                          if (numberMatch) {
                            // Render the number as part of the text
                            return (
                              <span
                                key={idx}
                                style={{ display: "block", marginBottom: 15 }}
                              >
                                <strong>{numberMatch[1]} </strong>
                                {renderWithLinks(
                                  line.trim().replace(/^(\d+\.)\s*/, "")
                                )}
                              </span>
                            );
                          } else {
                            // Render as normal text
                            return (
                              <span
                                key={idx}
                                style={{ display: "block", marginBottom: 8 }}
                              >
                                {renderWithLinks(line)}
                              </span>
                            );
                          }
                        });
                      })()}
                    </AccordionDetails>
                  )}
                </Accordion>
              ))}
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
};

export default FAQPage;
